import React, { useEffect, useRef, useState } from "react";

import { FormattedMessage, IntlFormatters, useIntl } from "react-intl";

import { barSortingTask } from "../../../common/sampleTasks";
import Seo from "../../../common/helpers/seo";

import Icon from "../../icon";

import Tooltip from "../../components/Tooltip";

import UpperRow from "../../pages/plan/UpperRow";
import MiddleRow from "../../pages/plan/MiddleRow";
import Menu from "../../pages/plan/Menu";
import Tasks from "../../pages/plan/Tasks";

import { StyledArticle } from "../styles";
import { P1, P2, H2, H4 } from "../styles";

import {
    StyledSpring,
    ListIcon,
    Gap,
    FloatingPlay,
    FloatingMeta,
    FloatingSharePlay,
    FloatingRefresh,
    FloatingShare,
    FloatingPublish,
    DashedLine,
    InfoBox,
    LineEnding,
    Plan,
    ListRow,
    Process,
} from "./styles";
import messages from "./messages";
import { IPlan, ITask } from "../../../common/types";
import { IUser } from "../../store/user/models";

const demoTask = barSortingTask;

const ListingArticle = () => {
    const { formatMessage, locale } = useIntl();
    const [openTab, setOpenTab] = useState(0);
    const [plan, setPlan] = useState<IPlan>(stages(formatMessage)[0]);
    const [opacity, setOpacity] = useState(1);
    const [transition, setTransition] = useState(500);
    const timer = useRef<ReturnType< typeof setTimeout>>(null);

    const goNext = () => {
        setOpenTab(oldOpenTab => {
            let newOpenTab = oldOpenTab + 1;
            if (newOpenTab === 6) newOpenTab = 0;
            selectState(newOpenTab, true);
            clearTimeout(timer.current);
            timer.current = setTimeout(goNext, 5000);
            return newOpenTab;
        })
    };

    const selectState = (index: number, triggered?: boolean) => {
        if (!triggered) {
            clearTimeout(timer.current);
        }
        setOpenTab(index);
        setPlan(stages(formatMessage)[index]);
        setOpacity(0);
        setTransition(0);
        setTimeout(() => {
            setOpacity(1);
            setTransition(500);
        });
    };
    useEffect(() => {
        timer.current = setTimeout(goNext, 5000);
        return () => {
            if (timer.current) {
                clearTimeout(timer.current);
            }
        }
    }, []);

    return (
        <StyledSpring withShadow marginTop={50}>
            <StyledArticle dark>
                <Seo
                    title={formatMessage(messages.title)}
                    type={"article"}
                    description={formatMessage(messages.description)}
                />
                <H2 dark>
                    <FormattedMessage {...messages.title} />
                </H2>
                <P1 center dark>
                    <FormattedMessage {...messages.description} />
                </P1>
                <Process>
                    <ListRow>
                        <ListIcon $isActive={openTab === 0} onClick={() => selectState(0)}>
                            <Icon i="edit" />
                            <FloatingPlay>
                                <Tooltip label={<FormattedMessage id="planCard.playTest" />}>
                                    <Icon i="playlarge" />
                                </Tooltip>
                                <DashedLine />
                            </FloatingPlay>
                            <FloatingMeta>
                                <Tooltip label={<FormattedMessage id="plan.label" />}>
                                    <Icon i="meta" />
                                </Tooltip>
                                <DashedLine short>
                                    <LineEnding />
                                </DashedLine>
                            </FloatingMeta>
                        </ListIcon>
                        <Gap></Gap>
                        <ListIcon $isActive={openTab === 1} onClick={() => selectState(1)}>
                            <Icon i="meta" />
                            <FloatingSharePlay>
                                <Tooltip label={<FormattedMessage id="planCard.playShareTest" />}>
                                    <Icon i="whiteboard" />
                                </Tooltip>
                                <DashedLine long />
                            </FloatingSharePlay>
                            <FloatingRefresh>
                                <Tooltip label={<FormattedMessage id="shareModal.refresh" />}>
                                    <Icon i="refresh" />
                                </Tooltip>
                                <DashedLine long />
                            </FloatingRefresh>
                            <FloatingShare>
                                <Tooltip label={<FormattedMessage id="planCard.share" />}>
                                    <Icon i="share" />
                                </Tooltip>
                                <DashedLine long />
                            </FloatingShare>
                            <FloatingPublish>
                                <Tooltip label={<FormattedMessage id="planCard.publish" />}>
                                    <Icon i="publish" />
                                </Tooltip>
                                <DashedLine long stopped>
                                    <LineEnding />
                                </DashedLine>
                            </FloatingPublish>
                        </ListIcon>
                        <Gap></Gap>
                        <ListIcon $isActive={openTab === 2} onClick={() => selectState(2)}>
                            <Icon i="refresh" />
                        </ListIcon>
                        <Gap></Gap>
                        <ListIcon $isActive={openTab === 3} onClick={() => selectState(3)}>
                            <Icon i="publish" />
                        </ListIcon>
                        <Gap></Gap>
                        <ListIcon $isActive={openTab === 4} onClick={() => selectState(4)}>
                            <Icon i="clock" />
                        </ListIcon>
                        <Gap></Gap>
                        <ListIcon $isActive={openTab === 5} onClick={() => selectState(5)}>
                            <Icon i="browse" />
                        </ListIcon>
                    </ListRow>
                    <InfoBox opacity={opacity} transition={transition}>
                        <H4>
                            <FormattedMessage {...messages["subtitle" + (openTab + 1)]} />
                        </H4>
                        <P2>
                            <FormattedMessage {...messages["description" + (openTab + 1)]} />
                        </P2>
                    </InfoBox>
                </Process>
                <Plan>
                    <UpperRow
                        plan={plan}
                        simulation
                        user={{ language: locale } as IUser}
                        publicView={false}
                        notLoggedIn={false}
                        showTutorial={false}
                        toggleTutorial={() => null}
                    />
                    <MiddleRow
                        plan={plan}
                        simulation
                        publicView={false}
                        user={{} as IUser}
                    />
                    <Menu
                        plan={plan}
                        simulation
                        pulsatingMeta={openTab === 1}
                        pulsatingRefresh={openTab === 2}
                        pulsatingPublish={openTab === 3}
                        type={"private"}
                        publicView={false}
                        notLoggedIn={false}
                    />
                    <Tasks
                        plan={plan}
                        simulation
                        pulsatingNewTask={openTab === 0}
                        publicView={false}
                    />
                </Plan>
            </StyledArticle>
        </StyledSpring>
    );
};

export default ListingArticle;

const names = (fm: IntlFormatters["formatMessage"]) => ({
    packName: fm(messages.firstCollection),
    name: {
        en: fm(messages.firstTest),
    },
    description: {},
    _id: "",
    scores: [],
    experience: {positions: [], enabled: false},
    created: 1,
    user: "",
    meta: {
        opus: {},
        rhythm: {},
        pitch: {},
        teaching: {},
    },
});

const playable = (fm: IntlFormatters["formatMessage"]) => ({
    ...names(fm),
    tasks: [demoTask as ITask],
    playable: true,
    publicId: "",
});

const stages = (fm: IntlFormatters["formatMessage"]): IPlan[] => [
    {
        ...names(fm),
        tasks: [],
    },
    {
        ...playable(fm),
        sharable: true,
    },
    {
        ...playable(fm),
        shared: true,
        sharable: true,
        metaDone: true,
        modified: 500,
        published: 0,
    },
    {
        ...playable(fm),
        shared: true,
        sharable: true,
        metaDone: true,
        modified: 500,
        published: 0,
    },
    {
        ...playable(fm),
        shared: true,
        sharable: true,
        metaDone: true,
        public: true,
        modified: 500,
        published: 0,
    },
    {
        ...playable(fm),
        shared: true,
        sharable: true,
        metaDone: true,
        public: true,
        accepted: true,
        modified: 500,
        published: 0,
    },
];
